import React, { ReactNode } from 'react';

import { useMediaQuery } from '@/hooks/use-media-query';
import { Drawer, DrawerContent } from '../shadcn/drawer';
import { Dialog, DialogClose, DialogContent } from '../shadcn/dialog';
import Text from '@/components/basic/text';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Button } from '@/components/shadcn/button';
import { Cross2Icon } from '@radix-ui/react-icons';

interface ResponsiveWrapperProps {
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

const ModalDrawer: React.FC<ResponsiveWrapperProps> = ({
  children,
  open,
  onOpenChange,
  className,
}) => {
  const isDesktop = useMediaQuery('(min-width: 640px)');

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn('max-h-[100vh] overflow-y-auto p-0', className)}
        >
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={className}>{children}</DrawerContent>
    </Drawer>
  );
};

interface ModalDrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  iconSrc?: string;
  heading?: string;
  description?: string;
}

const ModalDrawerHeader = ({
  iconSrc,
  heading,
  description,
  children,
  className,
  ...props
}: ModalDrawerHeaderProps) => {
  const isDesktop = useMediaQuery('(min-width: 640px)');

  return (
    <div
      className={cn(
        `flex h-[3.75rem] items-center justify-between px-6 sm:h-[4.25rem]`,
        heading && 'border-b border-primary-100',
        className
      )}
      {...props}
    >
      <div className='flex items-center gap-3'>
        {iconSrc && (
          <Image className='hidden h-8 w-8 sm:block' src={iconSrc} alt='icon' />
        )}
        <div className='flex flex-col gap-1'>
          {heading && (
            <Text variant={isDesktop ? 'h4' : 'h5'} weight='semibold'>
              {heading}
            </Text>
          )}
          {description && (
            <Text variant='body2' weight='medium' className='hidden sm:block'>
              {description}
            </Text>
          )}
        </div>
      </div>
      <DialogClose
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Cross2Icon className='h-5 w-5' />
        <span className='sr-only'>Close</span>
      </DialogClose>
    </div>
  );
};
ModalDrawerHeader.displayName = 'ModalDrawerHeader';

interface ModalDrawerFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  primaryAction: () => void;
  secondaryAction?: () => void;
  tertiaryAction?: () => void;
  primaryActionText: string;
  secondaryActionText?: string;
  tertiaryActionText?: string;
}

const ModalDrawerFooter = ({
  primaryActionText,
  secondaryActionText,
  tertiaryActionText,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  className,
  ...props
}: ModalDrawerFooterProps) => {
  return (
    <div
      className={cn(
        'flex items-center gap-4 px-6',
        !tertiaryActionText ? 'sm:justify-end' : 'sm:justify-between',
        secondaryActionText && tertiaryActionText
          ? 'flex-col-reverse items-stretch gap-4 py-4 sm:h-[4.25rem] sm:flex-row sm:justify-between'
          : 'h-[4.25rem]',
        className
      )}
      {...props}
    >
      {tertiaryActionText && (
        <div className='flex flex-1 sm:flex-none'>
          <Button
            variant='ghost'
            onClick={tertiaryAction}
            className={cn('flex-1 sm:flex-none')}
          >
            {tertiaryActionText}
          </Button>
        </div>
      )}
      <div
        className={cn(
          'flex flex-1 gap-4 sm:flex-none',
          secondaryActionText && tertiaryActionText
            ? 'flex-col-reverse sm:flex-row'
            : 'flex-row'
        )}
      >
        {secondaryActionText && (
          <Button
            variant='secondary'
            className='flex-1 sm:flex-none'
            onClick={secondaryAction}
          >
            {secondaryActionText}
          </Button>
        )}
        <Button
          variant='primary'
          className='flex-1 sm:flex-none'
          onClick={primaryAction}
        >
          {primaryActionText}
        </Button>
      </div>
    </div>
  );
};
ModalDrawerFooter.displayName = 'ModalDrawerFooter';

const ModalDrawerContent = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn('px-6 py-5 sm:py-6', className)} {...props}>
      {children}
    </div>
  );
};
ModalDrawerContent.displayName = 'ModalDrawerContent';

export default ModalDrawer;
export { ModalDrawerHeader, ModalDrawerFooter, ModalDrawerContent };
